import { useState, useRef, useEffect } from "react";
import {
  Calendar,
  FileText,
  FlaskConical,
  MessageCircle,
  ClipboardList,
  Paperclip,
  Users,
  Clock,
} from "lucide-react";
import api from "../api/client";
import { statusClasses } from "../constants/statusColors";

const DEFAULT_STATUSES = [
  "Applied",
  "Screening",
  "Interview",
  "Technical test",
  "Final interview",
  "Offer received",
  "Accepted",
  "Rejected",
  "Withdrawn",
];

const TABS = ["Details", "Feed", "Status", "Notes"];

const ACTION_ICONS = {
  meeting: Calendar,
  document_request: FileText,
  assessment: FlaskConical,
  info: MessageCircle,
};

function formatClosingDate(applicationPeriodEnd) {
  if (!applicationPeriodEnd) return null;

  const closeDate = new Date(applicationPeriodEnd);
  const now = new Date();

  // Treat both as UTC midnight for stable day diff
  const closeUTC = Date.UTC(
    closeDate.getFullYear(),
    closeDate.getMonth(),
    closeDate.getDate(),
  );
  const nowUTC = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.floor((nowUTC - closeUTC) / (1000 * 60 * 60 * 24));

  if (days < 0) {
    return {
      text: `Closes ${closeDate.toLocaleDateString("en-SE", {
        month: "short",
        day: "numeric",
      })}`,
      tone: "neutral",
    };
  }

  if (days === 0) {
    return { text: "Closes today", tone: "neutral" };
  }

  if (days === 1) {
    return { text: "Posting closed yesterday", tone: "amber" };
  }

  return {
    text: `Posting closed ${days} days ago`,
    tone: days < 14 ? "amber" : "neutral",
  };
}

function ProcessPipeline({ steps, currentStatus, statuses }) {
  if (!steps || steps.length === 0) return null;

  const completedSteps = new Set(
    (statuses ?? []).map((s) => s.status.toLowerCase()),
  );

  const currentIdx = (() => {
    for (let i = steps.length - 1; i >= 0; i--) {
      if (completedSteps.has(steps[i].toLowerCase())) return i;
    }
    return -1;
  })();

  const stepTimestamp = (step) => {
    const match = (statuses ?? []).find(
      (s) => s.status.toLowerCase() === step.toLowerCase(),
    );
    if (!match) return null;
    return new Date(match.created_at).toLocaleDateString("en-SE", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="mb-6 bg-gray-50 dark:bg-brand-dark-bg rounded-xl p-4">
      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-4">
        Hiring process
      </p>
      <div className="flex flex-col">
        {steps.map((step, idx) => {
          const isComplete = currentIdx >= idx;
          const isCurrent = currentIdx === idx;
          const isFuture = currentIdx < idx;
          const ts = stepTimestamp(step);
          const isLast = idx === steps.length - 1;

          return (
            <div key={idx} className="flex items-start gap-3">
              <div className="flex flex-col items-center flex-shrink-0">
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold z-10 transition ${
                    isComplete
                      ? "bg-brand-violet text-white shadow-sm shadow-brand-violet/30"
                      : "bg-gray-100 dark:bg-white/5 text-gray-300 dark:text-gray-600 border border-dashed border-gray-200 dark:border-white/10"
                  }`}
                >
                  {isComplete && !isCurrent ? (
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={3}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  ) : (
                    idx + 1
                  )}
                </div>
                {!isLast && (
                  <div
                    className={`w-0.5 h-8 mt-1 ${isComplete && !isCurrent ? "bg-brand-violet" : "bg-gray-200 dark:bg-white/10"}`}
                  />
                )}
              </div>
              <div className="pb-6 flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className={`text-sm font-medium ${
                      isComplete
                        ? "text-gray-900 dark:text-white"
                        : isFuture
                          ? "text-gray-300 dark:text-gray-600"
                          : "text-brand-violet dark:text-brand-violet-on-dark"
                    }`}
                  >
                    {step}
                  </span>
                  {isCurrent && (
                    <span className="text-xs bg-brand-violet/10 text-brand-violet dark:text-brand-violet-on-dark px-2 py-0.5 rounded-full font-medium">
                      Current stage
                    </span>
                  )}
                  {isFuture && (
                    <span className="text-xs text-gray-300 dark:text-gray-600 italic">
                      Upcoming
                    </span>
                  )}
                </div>
                {ts && <p className="text-xs text-gray-400 mt-0.5">{ts}</p>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EmployerMessageModal({ message, onClose }) {
  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div className="fixed inset-0 flex items-center justify-center z-50 px-4">
        <div className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-100 dark:border-white/10 shadow-2xl w-full max-w-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-brand-violet/10 flex items-center justify-center">
                <svg
                  className="w-4 h-4 text-brand-violet dark:text-brand-violet-on-dark"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                  />
                </svg>
              </div>
              <p className="font-semibold text-gray-900 dark:text-white text-sm">
                Message from employer
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <div className="bg-brand-violet-tint dark:bg-brand-violet/10 rounded-xl px-4 py-3">
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
              {message}
            </p>
          </div>
          <button
            onClick={onClose}
            className="mt-4 w-full border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 py-2 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-white/5 transition"
          >
            Close
          </button>
        </div>
      </div>
    </>
  );
}

function ActivityItem({ action, onToggleComplete }) {
  const IconComponent = ACTION_ICONS[action.action_type] ?? ClipboardList;
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  // The seeker's answer from their Inbox. A declined action stays in the
  // timeline (the employer still sees it) but reads as closed.
  const declined = action.response === "declined" || action.response === "dismissed";

  useEffect(() => {
    if (action.action_type === "document_request") {
      api
        .get(`/applications/actions/${action.id}/attachments`)
        .then(({ data }) => setAttachments(data))
        .catch(() => {});
    }
  }, [action.id, action.action_type]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post(
        `/applications/actions/${action.id}/attachments`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setAttachments((prev) => [...prev, data]);
    } catch (err) {
      alert(err.response?.data?.detail ?? "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleDelete = async (attachmentId) => {
    try {
      await api.delete(
        `/applications/actions/${action.id}/attachments/${attachmentId}`,
      );
      setAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
    } catch {
      alert("Could not delete file");
    }
  };

  const formatDate = (iso) => {
    if (!iso) return null;
    return new Date(iso).toLocaleString("en-SE", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  const formatBytes = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const calendarUrl = action.meeting_at
    ? (() => {
        const start = new Date(action.meeting_at);
        const end = new Date(
          start.getTime() + (action.duration_minutes ?? 60) * 60000,
        );
        const fmt = (d) =>
          d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
        const attendees = action.attendees?.join(", ") ?? "";
        return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(action.title)}&dates=${fmt(start)}/${fmt(end)}&details=${encodeURIComponent(attendees)}`;
      })()
    : null;

  return (
    <div
      className={`rounded-xl border p-4 transition ${
        action.is_completed || declined
          ? "border-gray-100 dark:border-white/5 bg-gray-50 dark:bg-white/5 opacity-60"
          : "border-gray-100 dark:border-white/10 bg-white dark:bg-brand-dark-surface"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <IconComponent className="w-5 h-5 flex-shrink-0 mt-0.5 text-gray-400" />
          <div className="flex-1 min-w-0">
            <p
              className={`text-sm font-medium ${action.is_completed ? "line-through text-gray-400" : "text-gray-900 dark:text-white"}`}
            >
              {action.title}
            </p>
            {action.description && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                {action.description}
              </p>
            )}
            {action.meeting_at && (
              <p className="text-xs text-brand-violet dark:text-brand-violet-on-dark mt-1 font-medium flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {formatDate(action.meeting_at)}
                {action.duration_minutes && ` · ${action.duration_minutes} min`}
              </p>
            )}
            {action.attendees?.length > 0 && (
              <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                <Users className="w-3 h-3" />
                {action.attendees.join(", ")}
              </p>
            )}
            {action.deadline && (
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 font-medium flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Deadline: {formatDate(action.deadline)}
              </p>
            )}
            {action.guidelines && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 italic">
                {action.guidelines}
              </p>
            )}
            {action.url && (
              <a
                href={action.url}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-brand-violet dark:text-brand-violet-on-dark hover:underline mt-1 inline-block"
              >
                Open link →
              </a>
            )}
            {calendarUrl && !action.is_completed && !declined && (
              <a
                href={calendarUrl}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-brand-violet dark:text-brand-violet-on-dark hover:underline mt-1 inline-block ml-3"
              >
                + Add to Google Calendar
              </a>
            )}

            {/* File attachments — document_request only */}
            {action.action_type === "document_request" && (
              <div className="mt-3 pt-3 border-t border-gray-100 dark:border-white/10">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                  Attachments
                </p>
                {attachments.length > 0 && (
                  <div className="flex flex-col gap-1.5 mb-2">
                    {attachments.map((a) => (
                      <div
                        key={a.id}
                        className="flex items-center justify-between gap-2 bg-gray-50 dark:bg-brand-dark-bg rounded-lg px-3 py-2"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <Paperclip className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" />
                          <a
                            href={a.url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-brand-violet dark:text-brand-violet-on-dark hover:underline truncate"
                          >
                            {a.filename}
                          </a>
                          <span className="text-xs text-gray-400 flex-shrink-0">
                            {formatBytes(a.file_size)}
                          </span>
                        </div>
                        <button
                          onClick={() => handleDelete(a.id)}
                          className="text-gray-300 hover:text-red-500 transition flex-shrink-0"
                        >
                          <svg
                            className="w-3.5 h-3.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  className="hidden"
                  onChange={handleUpload}
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="text-xs text-brand-violet dark:text-brand-violet-on-dark hover:underline disabled:opacity-50 flex items-center gap-1"
                >
                  {uploading ? (
                    "Uploading..."
                  ) : (
                    <>
                      <span>+</span> Attach file
                    </>
                  )}
                </button>
                <p className="text-xs text-gray-400 mt-1">
                  PDF, Word, JPEG or PNG · max 5MB
                </p>
              </div>
            )}
            {action.response && (
              <span
                className={`inline-flex items-center gap-1 text-xs font-medium mt-2 px-2 py-0.5 rounded-full ${
                  action.response === "accepted" ||
                  action.response === "submitted"
                    ? "bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400"
                    : "bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-400"
                }`}
              >
                {action.response === "accepted" ||
                action.response === "submitted"
                  ? "✓ "
                  : "✕ "}
                {action.response}
              </span>
            )}
            <p className="text-xs text-gray-300 dark:text-gray-600 mt-2">
              {formatDate(action.created_at)}
            </p>
          </div>
        </div>
        <button
          onClick={onToggleComplete}
          className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition ${
            action.is_completed
              ? "border-brand-violet dark:border-brand-violet-on-dark bg-brand-violet text-white"
              : "border-gray-300 dark:border-gray-600 hover:border-brand-violet dark:hover:border-brand-violet-on-dark"
          }`}
        >
          {action.is_completed && (
            <svg
              className="w-3 h-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={3}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
}

export default function ApplicationCard({
  app,
  expanded,
  onToggle,
  onEdit,
  onDelete,
  onToggleFavourite,
  onStatusAdded,
  defaultTab,
}) {
  const [activeTab, setActiveTab] = useState(defaultTab ?? "Details");
  const [newStatus, setNewStatus] = useState("");
  const [customStatus, setCustomStatus] = useState("");
  const [statusNote, setStatusNote] = useState("");
  const [addingStatus, setAddingStatus] = useState(false);
  const [feedArticles, setFeedArticles] = useState(null);
  const [feedLoading, setFeedLoading] = useState(false);
  const [feedError, setFeedError] = useState(false);
  const [actions, setActions] = useState(null);
  const [actionsLoading, setActionsLoading] = useState(false);
  const [showMsgModal, setShowMsgModal] = useState(false);

  useEffect(() => {
    if (defaultTab) setActiveTab(defaultTab);
  }, [defaultTab]);

  const currentStatus = app.current_status ?? "Applied";
  const colorClass =
    statusClasses(currentStatus);
  const employerMessage = app.employer_message ?? null;
  const seekerNotes = app.notes ?? null;
  // Employer edits to the position, stored as info actions by the PATCH
  // endpoint. Shown in Notes as a read-only log next to the seeker's own
  // notes, never written into the notes column itself.
  const POSITION_UPDATE_TITLE = "Position details updated";
  const positionUpdates = (actions ?? [])
    .filter((a) => a.title === POSITION_UPDATE_TITLE)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  const closing = formatClosingDate(app.application_period_end);

  const locationParts = [
    app.location_city,
    app.location_region,
    app.location_country,
    app.location_global_area,
  ].filter(Boolean);

  useEffect(() => {
    if (expanded && actions === null && !actionsLoading) {
      loadActions();
    }
  }, [expanded]);

  const handleAddStatus = async () => {
    const status = newStatus === "__custom__" ? customStatus : newStatus;
    if (!status.trim()) return;
    setAddingStatus(true);
    try {
      await api.post(`/applications/${app.id}/statuses`, {
        status,
        note: statusNote || null,
      });
      setNewStatus("");
      setCustomStatus("");
      setStatusNote("");
      onStatusAdded();
    } finally {
      setAddingStatus(false);
    }
  };

  const loadFeed = () => {
    setFeedLoading(true);
    setFeedError(false);
    api
      .get(`/feed/${app.id}`)
      .then(({ data }) => setFeedArticles(data))
      .catch(() => setFeedError(true))
      .finally(() => setFeedLoading(false));
  };

  const loadActions = () => {
    setActionsLoading(true);
    api
      .get(`/applications/by-id/${app.id}/actions`)
      .then(({ data }) => setActions(data))
      .catch(() => setActions([]))
      .finally(() => setActionsLoading(false));
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    if (tab === "Feed" && feedArticles === null && !feedLoading) loadFeed();
    if (tab === "Status" && !actionsLoading) {
      api
        .patch(`/applications/by-id/${app.id}/statuses/mark-read`)
        .catch(() => {});
    }
  };

  return (
    <>
      <div className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-200 dark:border-white/10 shadow-sm dark:shadow-none overflow-hidden transition-shadow hover:shadow-md">
        {" "}
        {/* ── Accordion header ── */}
        <div
          className="flex items-center gap-4 px-5 py-4 cursor-pointer select-none"
          onClick={onToggle}
        >
          <div className="w-10 h-10 rounded-xl bg-brand-violet/20 flex items-center justify-center text-brand-violet dark:text-brand-violet-on-dark font-bold text-lg flex-shrink-0">
            {app.company[0].toUpperCase()}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-sm truncate">
                {app.job_title}
              </span>
              <span className="text-gray-400 text-sm">·</span>
              <span className="text-gray-500 dark:text-gray-400 text-sm truncate">
                {app.company}
              </span>
              {locationParts.length > 0 && (
                <>
                  <span className="text-gray-400 text-sm">·</span>
                  <span className="text-gray-400 text-xs">
                    {locationParts.join(", ")}
                  </span>
                </>
              )}
              {app.is_remote && (
                <span className="text-xs bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded-full">
                  Remote
                </span>
              )}
              {app.job_position_id && (
                <span
                  className="text-xs text-gray-500 dark:text-gray-400 px-2 py-0.5 rounded-full border border-gray-200 dark:border-white/10 flex items-center gap-1"
                  title={
                    app.application_source === "arena"
                      ? "Applied by browsing the Arena"
                      : "Submitted via Joborigo"
                  }
                >
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  {app.application_source === "arena" ? "via Arena" : "via Joborigo"}
                </span>
              )}
            </div>

            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span
                className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${colorClass}`}
              >
                {currentStatus}
              </span>
              <span className="text-xs text-gray-400">
                {new Date(app.applied_at).toLocaleDateString("en-SE", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}
              </span>
              {closing && (
                <span
                  className={`text-xs ${
                    closing.tone === "amber"
                      ? "text-amber-600 dark:text-amber-400"
                      : "text-gray-400"
                  }`}
                >
                  {closing.text}
                </span>
              )}
              {app.salary_min && (
                <span className="text-xs text-gray-400">
                  {app.salary_min.toLocaleString()}–
                  {app.salary_max?.toLocaleString()} {app.salary_currency}
                </span>
              )}
              {app.ghosting?.is_ghost_risk && (
                <span className="text-xs bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 px-2 py-0.5 rounded-full flex items-center gap-1">
                  <span className="text-sm leading-none">👻</span>
                  {app.ghosting.warning_message}
                </span>
              )}
              {employerMessage && (
                <span className="text-xs bg-brand-violet/10 text-brand-violet dark:text-brand-violet-on-dark px-2 py-0.5 rounded-full flex items-center gap-1">
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  Employer message
                </span>
              )}
            </div>
          </div>

          <div
            className="flex items-center gap-1 flex-shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={onToggleFavourite}
              className={`p-1.5 rounded-lg transition ${app.is_favourite ? "text-amber-400" : "text-gray-300 dark:text-gray-600 hover:text-amber-400"}`}
            >
              <svg
                className="w-4 h-4"
                fill={app.is_favourite ? "currentColor" : "none"}
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                />
              </svg>
            </button>
            <button
              onClick={onEdit}
              className="p-1.5 rounded-lg text-gray-400 hover:text-brand-violet dark:hover:text-brand-violet-on-dark transition"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </button>
            <button
              onClick={onDelete}
              className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 transition"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
            <svg
              className={`w-4 h-4 text-gray-400 transition-transform duration-200 ml-1 ${expanded ? "rotate-180" : ""}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>
        {/* ── Expanded content ── */}
        {expanded && (
          <div className="border-t border-gray-100 dark:border-white/10">
            <div className="flex border-b border-gray-100 dark:border-white/10 px-5">
              {TABS.map((tab) => (
                <button
                  key={tab}
                  onClick={() => handleTabClick(tab)}
                  className={`py-3 px-4 text-sm font-medium border-b-2 transition -mb-px ${
                    activeTab === tab
                      ? "border-brand-violet dark:border-brand-violet-on-dark text-brand-violet dark:text-brand-violet-on-dark"
                      : "border-transparent text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            <div className="p-5">
              {/* ── Details tab ── */}
              {activeTab === "Details" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
                  <div>
                    <p className="text-gray-400 text-xs mb-0.5">Company</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {app.company}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs mb-0.5">Job title</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {app.job_title}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs mb-0.5">Applied</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {new Date(app.applied_at).toLocaleDateString("en-SE")}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs mb-0.5">Apply by</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {app.application_period_end
                        ? new Date(
                            app.application_period_end,
                          ).toLocaleDateString("en-SE")
                        : "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs mb-0.5">Cover letter</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {app.cover_letter_status === "submitted_self"
                        ? "Submitted"
                        : "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs mb-0.5">Contact</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {app.contact_person || "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs mb-0.5">Location</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {locationParts.join(", ") || "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs mb-0.5">Remote</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {app.is_remote ? "Yes" : "No"}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs mb-0.5">Global area</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {app.location_global_area || "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs mb-0.5">Salary range</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {app.salary_min
                        ? `${app.salary_min.toLocaleString()}–${app.salary_max?.toLocaleString()} ${app.salary_currency}`
                        : "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs mb-0.5">Job URL</p>
                    {app.job_url ? (
                      <a
                        href={app.job_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-brand-violet dark:text-brand-violet-on-dark hover:underline font-medium"
                      >
                        View posting ↗
                      </a>
                    ) : (
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        —
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* ── Feed tab ── */}
              {activeTab === "Feed" && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-widest">
                      Latest news for {app.company}
                    </p>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">
                        via Google News
                      </span>
                      <button
                        onClick={loadFeed}
                        className="text-xs text-brand-violet dark:text-brand-violet-on-dark hover:underline"
                      >
                        Refresh
                      </button>
                    </div>
                  </div>
                  {feedLoading && (
                    <div className="flex flex-col gap-4">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="animate-pulse">
                          <div className="h-4 bg-gray-100 dark:bg-white/5 rounded w-3/4 mb-2" />
                          <div className="h-3 bg-gray-100 dark:bg-white/5 rounded w-1/2" />
                        </div>
                      ))}
                    </div>
                  )}
                  {feedError && (
                    <div className="text-center py-8">
                      <p className="text-gray-400 text-sm">
                        Could not load news feed.
                      </p>
                      <button
                        onClick={loadFeed}
                        className="text-xs text-brand-violet dark:text-brand-violet-on-dark hover:underline mt-2"
                      >
                        Try again
                      </button>
                    </div>
                  )}
                  {!feedLoading &&
                    !feedError &&
                    feedArticles !== null &&
                    feedArticles.length === 0 && (
                      <div className="text-center py-8">
                        <p className="text-gray-400 text-sm">
                          No recent news found for {app.company}.
                        </p>
                      </div>
                    )}
                  {!feedLoading &&
                    !feedError &&
                    feedArticles &&
                    feedArticles.length > 0 && (
                      <div className="flex flex-col gap-3">
                        {feedArticles.map((article, i) => (
                          <a
                            key={i}
                            href={article.url}
                            target="_blank"
                            rel="noreferrer"
                            className="group block p-4 bg-gray-50 dark:bg-brand-dark-bg rounded-xl border border-transparent hover:border-brand-violet/30 dark:hover:border-brand-violet-on-dark/30 transition"
                          >
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 group-hover:text-brand-violet dark:group-hover:text-brand-violet-on-dark transition leading-snug mb-1.5">
                              {article.title}
                            </p>
                            {article.summary && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mb-2">
                                {article.summary}
                              </p>
                            )}
                            <div className="flex items-center gap-2">
                              {article.source && (
                                <span className="text-xs font-medium text-gray-400">
                                  {article.source}
                                </span>
                              )}
                              {article.source && article.published_at && (
                                <span className="text-gray-300 dark:text-gray-600 text-xs">
                                  ·
                                </span>
                              )}
                              {article.published_at && (
                                <span className="text-xs text-gray-400">
                                  {article.published_at}
                                </span>
                              )}
                              <span className="text-gray-300 dark:text-gray-600 text-xs">
                                ·
                              </span>
                              <span className="text-xs text-brand-violet dark:text-brand-violet-on-dark opacity-0 group-hover:opacity-100 transition">
                                Read article ↗
                              </span>
                            </div>
                          </a>
                        ))}
                        <div className="mt-2 p-4 rounded-xl border border-dashed border-brand-violet/30 dark:border-brand-violet-on-dark/30 bg-brand-violet-tint dark:bg-brand-violet/10 text-center">
                          <p className="text-xs font-medium text-brand-violet dark:text-brand-violet-on-dark mb-0.5">
                            Pro feature coming soon
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            AI-powered summaries, sentiment analysis, and hiring
                            signal detection
                          </p>
                        </div>
                      </div>
                    )}
                </div>
              )}

              {/* ── Status tab ── */}
              {activeTab === "Status" && (
                <div>
                  {employerMessage && (
                    <div className="mb-5 bg-brand-violet-tint dark:bg-brand-violet/10 border border-brand-violet/20 dark:border-brand-violet-on-dark/20 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <svg
                          className="w-4 h-4 text-brand-violet dark:text-brand-violet-on-dark flex-shrink-0"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={1.5}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                          />
                        </svg>
                        <p className="text-xs font-semibold text-brand-violet dark:text-brand-violet-on-dark uppercase tracking-widest">
                          Message from employer
                        </p>
                      </div>
                      <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                        {employerMessage}
                      </p>
                    </div>
                  )}

                  {app.process_steps && app.process_steps.length > 0 && (
                    <ProcessPipeline
                      steps={app.process_steps}
                      currentStatus={currentStatus}
                      statuses={app.statuses}
                    />
                  )}

                  {app.ghosting?.is_ghost_risk && (
                    <div className="mb-5 flex items-start gap-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/30 rounded-xl p-4">
                      <span className="text-xl leading-none flex-shrink-0 mt-0.5">
                        👻
                      </span>
                      <div>
                        <p className="text-sm font-medium text-amber-700 dark:text-amber-400">
                          Ghosting risk detected
                        </p>
                        <p className="text-xs text-amber-600 dark:text-amber-500 mt-0.5">
                          {app.ghosting.warning_message}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="mb-6">
                    {app.statuses.length === 0 ? (
                      <p className="text-gray-400 text-sm">
                        No status history yet.
                      </p>
                    ) : (
                      <div className="flex flex-col">
                        {[...app.statuses].reverse().map((s, i) => {
                          const statusActions = (actions ?? []).filter(
                            (a) => a.application_status_id === s.id,
                          );
                          return (
                            <div key={s.id} className="grid grid-cols-2 gap-4">
                              <div className="flex items-start gap-3">
                                <div className="flex flex-col items-center pt-1">
                                  <div
                                    className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${i === 0 ? "bg-brand-violet" : "bg-gray-300 dark:bg-gray-600"}`}
                                  />
                                  {i < app.statuses.length - 1 && (
                                    <div className="w-px flex-1 min-h-8 bg-gray-200 dark:bg-white/10 mt-1" />
                                  )}
                                </div>
                                <div className="pb-6">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span
                                      className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusClasses(s.status)}`}
                                    >
                                      {s.status}
                                    </span>
                                    {(s.source === "employer" ||
                                      s.source === "agency") && (
                                      <span className="text-xs text-brand-violet dark:text-brand-violet-on-dark font-medium">
                                        via {s.source}
                                      </span>
                                    )}
                                  </div>
                                  {s.note && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                      {s.note}
                                    </p>
                                  )}
                                  <p className="text-xs text-gray-400 mt-0.5">
                                    {new Date(s.created_at).toLocaleDateString(
                                      "en-SE",
                                      {
                                        day: "numeric",
                                        month: "short",
                                        year: "numeric",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      },
                                    )}
                                  </p>
                                </div>
                              </div>
                              <div className="flex flex-col gap-2 pb-6">
                                {statusActions.length === 0 ? (
                                  <p className="text-xs text-gray-300 dark:text-gray-600 italic pt-1">
                                    No correspondence
                                  </p>
                                ) : (
                                  statusActions.map((action) => (
                                    <ActivityItem
                                      key={action.id}
                                      action={action}
                                      onToggleComplete={async () => {
                                        setActions((prev) =>
                                          prev.map((a) =>
                                            a.id === action.id
                                              ? {
                                                  ...a,
                                                  is_completed: !a.is_completed,
                                                }
                                              : a,
                                          ),
                                        );
                                        await api.patch(
                                          `/applications/actions/${action.id}/complete`,
                                        );
                                        loadActions();
                                      }}
                                    />
                                  ))
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="border-t border-gray-100 dark:border-white/10 pt-4">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-3">
                      Update status
                    </p>
                    <div className="flex flex-col gap-2">
                      <select
                        value={newStatus}
                        onChange={(e) => setNewStatus(e.target.value)}
                        className="border border-gray-200 dark:border-white/10 bg-white dark:bg-brand-dark-bg rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-violet dark:focus:ring-brand-violet-on-dark"
                      >
                        <option value="">Select new status...</option>
                        {app.process_steps && app.process_steps.length > 0 ? (
                          <>
                            <optgroup label="Employer process">
                              {app.process_steps.map((s) => (
                                <option key={s} value={s}>
                                  {s}
                                </option>
                              ))}
                            </optgroup>
                            <optgroup label="Other">
                              {DEFAULT_STATUSES.map((s) => (
                                <option key={s} value={s}>
                                  {s}
                                </option>
                              ))}
                            </optgroup>
                          </>
                        ) : (
                          DEFAULT_STATUSES.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))
                        )}
                        <option value="__custom__">+ Custom status...</option>
                      </select>
                      {newStatus === "__custom__" && (
                        <input
                          value={customStatus}
                          onChange={(e) => setCustomStatus(e.target.value)}
                          placeholder="Enter custom status"
                          className="border border-gray-200 dark:border-white/10 bg-white dark:bg-brand-dark-bg rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-violet dark:focus:ring-brand-violet-on-dark"
                        />
                      )}
                      <input
                        value={statusNote}
                        onChange={(e) => setStatusNote(e.target.value)}
                        placeholder="Optional note..."
                        className="border border-gray-200 dark:border-white/10 bg-white dark:bg-brand-dark-bg rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-violet dark:focus:ring-brand-violet-on-dark"
                      />
                      <button
                        onClick={handleAddStatus}
                        disabled={
                          addingStatus ||
                          !newStatus ||
                          (newStatus === "__custom__" && !customStatus.trim())
                        }
                        className="bg-brand-violet text-white py-2 rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-40 transition"
                      >
                        {addingStatus ? "Saving..." : "Update status"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Notes tab ── */}
              {activeTab === "Notes" && (
                <div className="flex flex-col gap-5">
                  {seekerNotes ? (
                    <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                      {seekerNotes}
                    </p>
                  ) : positionUpdates.length === 0 ? (
                    <p className="text-sm text-gray-400 italic">
                      No notes added. Edit the application to add notes.
                    </p>
                  ) : null}

                  {positionUpdates.length > 0 && (
                    <div className="flex flex-col gap-2">
                      <p className="text-xs font-medium text-gray-400 uppercase tracking-widest">
                        Updates from {app.company}
                      </p>
                      {positionUpdates.map((u) => (
                        <p
                          key={u.id}
                          className="text-sm leading-relaxed text-brand-violet dark:text-brand-violet-on-dark border-l-2 border-brand-violet/40 dark:border-brand-violet-on-dark/40 pl-3"
                        >
                          [
                          {new Date(u.created_at).toLocaleDateString("en-SE", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                          ] {u.description}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {showMsgModal && employerMessage && (
        <EmployerMessageModal
          message={employerMessage}
          onClose={() => setShowMsgModal(false)}
        />
      )}
    </>
  );
}
